
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  MessageSquare,
  Slack,
  Mail,
  Calendar,
  Video,
  Phone,
  FileText,
} from "lucide-react"
import { getOrgEqTrackersDashboard, getOrgEqTrackerHistory } from "../../api/helpers"

const sourceIcons = {
  slack: Slack,
  gmail: Mail,
  gcal: Calendar,
  zoom: Video,
  gong: Phone,
  voice: Phone,
  tickets: FileText,
  notes: MessageSquare,
  calls: Video,
  emails: Mail,
  chats: MessageSquare,
}

interface TrackerHit {
  hit_id: string
  tracker_id: string
  occurred_at: string
  evaluated_at: string
  confidence: number
  reasoning: any
  primary_emotion?: {
    emotion: string
    occurrences: number
    composite_score: number
  } | null
  content_type: string
  content_id: string
  content_details: {
    type: string
    id: string
    title: string
    timestamp: string
    duration?: number
    participants?: string[]
    meeting_id?: string
    host_email?: string
    excerpt?: string
  }
  tracker_name: string
}

interface TrackerActivity {
  tracker_id: string
  tracker_name: string
  description: string
  team: string | null
  start_date: string | null
  end_date: string | null
  total_hits: number
  trend: "up" | "down" | "stable"
  trend_percentage: number
  top_emotions: Array<{
    emotion: string
    percentage: number
    total_occurrences?: number
  }>
  positivity_percentage: number
  summary?: string
  summary_generated_at?: string
  last_triggered: string | null
  last_triggered_timestamp: string | null
  active: boolean
  sources?: string[]
  recent_hits?: TrackerHit[]
}

const getEmotionColor = (emotion: string) => {
  const lowerEmotion = emotion.toLowerCase()
  if (lowerEmotion.includes("optimism") || lowerEmotion.includes("satisfaction") || lowerEmotion.includes("relief") || lowerEmotion.includes("interest") || lowerEmotion.includes("joy") || lowerEmotion.includes("determined") || lowerEmotion.includes("excited") || lowerEmotion.includes("proud")) {
    return "bg-green-100 text-green-800"
  }
  if (lowerEmotion.includes("anticipation") || lowerEmotion.includes("curiosity") || lowerEmotion.includes("analytical") || lowerEmotion.includes("empathy") || lowerEmotion.includes("concentration")) {
    return "bg-purple-100 text-purple-800"
  }
  if (lowerEmotion.includes("concern") || lowerEmotion.includes("confusion") || lowerEmotion.includes("anxiety") || lowerEmotion.includes("uncertainty") || lowerEmotion.includes("annoyance") || lowerEmotion.includes("anger") || lowerEmotion.includes("distressed") || lowerEmotion.includes("tired") || lowerEmotion.includes("anxious") || lowerEmotion.includes("doubtful")) {
    return "bg-red-100 text-red-800"
  }
  if (lowerEmotion.includes("amusement")) {
    return "bg-orange-100 text-orange-800"
  }
  return "bg-gray-100 text-gray-800"
}

const getStatusConfig = (positivity: number | null | undefined, trend: string) => {
  const value = typeof positivity === "number" && !Number.isNaN(positivity) ? positivity : null
  if (value === null) {
    return {
      label: "no data",
      color: "text-gray-700 border-gray-300",
      icon: Minus,
      bgColor: "bg-gray-50",
    }
  }

  if (value >= 60) {
    return {
      label: "improving",
      color: "text-green-700 border-green-700",
      icon: TrendingUp,
      bgColor: "bg-green-50",
    }
  }

  if (value >= 40) {
    return {
      label: "no change",
      color: "text-purple-700 border-purple-700",
      icon: Minus,
      bgColor: "bg-purple-50",
    }
  }

  return {
    label: "at-risk",
    color: "text-red-700 border-red-700",
    icon: TrendingDown,
    bgColor: "bg-red-50",
  }
}

const getCardBgColor = (positivity: number | null | undefined) => {
  const value = typeof positivity === "number" && !Number.isNaN(positivity) ? positivity : null
  if (value === null) return "bg-gray-50"
  if (value >= 60) return "bg-green-50"
  if (value >= 40) return "bg-purple-50"
  return "bg-red-50"
}

const getScoreColor = (positivity: number | null | undefined) => {
  const value = typeof positivity === "number" && !Number.isNaN(positivity) ? positivity : null
  if (value === null) return "text-gray-500"
  if (value >= 60) return "text-green-600"
  if (value >= 40) return "text-purple-600"
  return "text-red-600"
}

interface WorkTopicSnapshotProps {
  organizationFilter?: string
  trackerFilter?: string
}

export function WorkTopicSnapshot({ organizationFilter, trackerFilter }: WorkTopicSnapshotProps) {
  const [trackerActivity, setTrackerActivity] = useState<TrackerActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<"day" | "week" | "month">("day")
  const [expandedTrackerId, setExpandedTrackerId] = useState<string | null>(null)
  const [loadingHits, setLoadingHits] = useState<Record<string, boolean>>({})
  const [trackerHits, setTrackerHits] = useState<Record<string, TrackerHit[]>>({})

  const user = useSelector((state: any) => state.auth.user)

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return null

    const start = new Date(startDate)
    const end = new Date(endDate)

    const formatDate = (date: Date) => {
      const month = date.toLocaleString('en-US', { month: 'short' })
      const day = date.getDate()
      const year = date.getFullYear()
      return `${month} ${day}, ${year}`
    }

    // If same year, show "Nov 1–Dec 15, 2024"
    if (start.getFullYear() === end.getFullYear()) {
      const startMonth = start.toLocaleString('en-US', { month: 'short' })
      const endMonth = end.toLocaleString('en-US', { month: 'short' })
      const year = start.getFullYear()

      if (start.getMonth() === end.getMonth()) {
        // Same month: "Nov 1–15, 2024"
        return `${startMonth} ${start.getDate()}–${end.getDate()}, ${year}`
      } else {
        // Different months: "Nov 1–Dec 15, 2024"
        return `${startMonth} ${start.getDate()}–${endMonth} ${end.getDate()}, ${year}`
      }
    } else {
      // Different years: "Nov 15, 2025–Jan 31, 2026"
      return `${formatDate(start)}–${formatDate(end)}`
    }
  }

  useEffect(() => {
    const loadTrackers = async () => {
      if (!user?.organization_info?._id) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getOrgEqTrackersDashboard(user.organization_info._id, period)
        setTrackerActivity(response?.trackers || [])
      } catch (err) {
        console.error("Failed to load tracker dashboard:", err)
      } finally {
        setLoading(false)
      }
    }

    loadTrackers()
  }, [user?.organization_info?._id, period])

  const handleTrackerClick = async (trackerId: string) => {
    // If clicking the same tracker, just collapse it
    if (expandedTrackerId === trackerId) {
      setExpandedTrackerId(null)
      return
    }

    // Expand the tracker
    setExpandedTrackerId(trackerId)

    // If we haven't fetched hits for this tracker yet, fetch them
    if (!trackerHits[trackerId] && !loadingHits[trackerId]) {
      setLoadingHits(prev => ({ ...prev, [trackerId]: true }))

      try {
        const response = await getOrgEqTrackerHistory(
          user.organization_info._id,
          trackerId,
          3 // Limit to 3 most recent hits
        )

        // The API returns recent_hits
        setTrackerHits(prev => ({ ...prev, [trackerId]: response?.recent_hits || [] }))
      } catch (error) {
        console.error("Failed to load tracker hits:", error)
        setTrackerHits(prev => ({ ...prev, [trackerId]: [] }))
      } finally {
        setLoadingHits(prev => ({ ...prev, [trackerId]: false }))
      }
    }
  }

  const getHitBorderColorClass = (emotion?: string) => {
    if (!emotion) return "gray"

    const lowerEmotion = emotion.toLowerCase()

    // Positive emotions - Green (matches getEmotionColor)
    if (lowerEmotion.includes("optimism") || lowerEmotion.includes("satisfaction") ||
        lowerEmotion.includes("relief") || lowerEmotion.includes("interest") ||
        lowerEmotion.includes("joy") || lowerEmotion.includes("determined") ||
        lowerEmotion.includes("excited") || lowerEmotion.includes("proud") ||
        lowerEmotion.includes("excite") || lowerEmotion.includes("satisf") ||
        lowerEmotion.includes("pride") || lowerEmotion.includes("delight") ||
        lowerEmotion.includes("enthusiasm")) {
      return "green"
    }

    // Analytical/Neutral emotions - Purple (matches getEmotionColor)
    if (lowerEmotion.includes("anticipation") || lowerEmotion.includes("curiosity") ||
        lowerEmotion.includes("analytical") || lowerEmotion.includes("empathy") ||
        lowerEmotion.includes("concentration")) {
      return "purple"
    }

    // Negative emotions - Red (matches getEmotionColor)
    if (lowerEmotion.includes("concern") || lowerEmotion.includes("confusion") ||
        lowerEmotion.includes("anxiety") || lowerEmotion.includes("uncertainty") ||
        lowerEmotion.includes("annoyance") || lowerEmotion.includes("anger") ||
        lowerEmotion.includes("distressed") || lowerEmotion.includes("tired") ||
        lowerEmotion.includes("anxious") || lowerEmotion.includes("doubtful") ||
        lowerEmotion.includes("frustrat") || lowerEmotion.includes("stress") ||
        lowerEmotion.includes("disappoint") || lowerEmotion.includes("defensive")) {
      return "red"
    }

    // Amusement - Orange (matches getEmotionColor)
    if (lowerEmotion.includes("amusement")) {
      return "orange"
    }

    // Default - Gray
    return "gray"
  }

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return `${diffMins} minutes ago`
    if (diffHours < 24) return `${diffHours} hours ago`
    return `${diffDays} days ago`
  }

  const getSummarizedReason = (reasoning: any) => {
    if (!reasoning || typeof reasoning !== 'object') return null
    const firstKey = Object.keys(reasoning)[0]
    if (!firstKey) return null
    return reasoning[firstKey]?.summarized_reason || null
  }

const getContentTypeDisplay = (type?: string) => {
    const typeMap: Record<string, { label: string; icon: keyof typeof sourceIcons }> = {
      'call': { label: 'Call', icon: 'calls' },
      'calls': { label: 'Call', icon: 'calls' },
      'video': { label: 'Call', icon: 'calls' },
      'zoom': { label: 'Call', icon: 'zoom' },
      'gong': { label: 'Call', icon: 'gong' },
      'voice': { label: 'Call', icon: 'voice' },
      'email': { label: 'Email', icon: 'emails' },
      'emails': { label: 'Email', icon: 'emails' },
      'gmail': { label: 'Email', icon: 'gmail' },
      'slack': { label: 'Slack', icon: 'slack' },
      'chat': { label: 'Slack', icon: 'slack' },
      'chats': { label: 'Slack', icon: 'chats' },
      'message': { label: 'Message', icon: 'chats' },
      'messages': { label: 'Message', icon: 'chats' },
    }

  const lowerType = typeof type === "string" ? type.toLowerCase() : ""
    const config = typeMap[lowerType] || { label: 'Message', icon: 'chats' as keyof typeof sourceIcons }
    const Icon = sourceIcons[config.icon]

    return { label: config.label, Icon }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6" />
            <h2 className="text-2xl font-semibold">Active Tracker Intelligence</h2>
            <span className="text-muted-foreground">(Live)</span>
          </div>
          <Select value={period} onValueChange={(value) => setPeriod(value as "day" | "week" | "month")}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Time period" />
            </SelectTrigger>
            <SelectContent className="z-[9999] bg-white text-black border border-gray-200 shadow-lg">
              <SelectItem value="day" className="text-black hover:bg-gray-100 cursor-pointer">Last 24 Hours</SelectItem>
              <SelectItem value="week" className="text-black hover:bg-gray-100 cursor-pointer">Last Week</SelectItem>
              <SelectItem value="month" className="text-black hover:bg-gray-100 cursor-pointer">Last Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading trackers...</p>
          </div>
        ) : trackerActivity.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No active trackers</h3>
            <p className="text-muted-foreground">
              Create your first tracker to start monitoring emotional intelligence across your organization.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {trackerActivity.map((tracker) => {
              const positivityValue =
                typeof tracker.positivity_percentage === "number" && !Number.isNaN(tracker.positivity_percentage)
                  ? tracker.positivity_percentage
                  : null
              const roundedPositivity = positivityValue !== null ? Math.round(positivityValue) : null
              const status = getStatusConfig(positivityValue, tracker.trend)
              const StatusIcon = status.icon
              const dateRange = formatDateRange(tracker.start_date, tracker.end_date)
              const isExpanded = expandedTrackerId === tracker.tracker_id

              return (
                <div key={tracker.tracker_id} className="space-y-3">
                  <div
                    className={`${getCardBgColor(positivityValue)} border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => handleTrackerClick(tracker.tracker_id)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold">{tracker.tracker_name}</h3>
                          {tracker.team && (
                            <Badge variant="outline" className="bg-white border-gray-300 text-xs">
                              {tracker.team}
                            </Badge>
                          )}
                          {dateRange && (
                            <Badge variant="outline" className="bg-white border-gray-300 text-xs">
                              {dateRange}
                            </Badge>
                          )}
                          {tracker.sources && tracker.sources.length > 0 && (
                            <div className="flex gap-1">
                              {tracker.sources.slice(0, 3).map((source) => {
                                const Icon = sourceIcons[source as keyof typeof sourceIcons]
                                return Icon ? <Icon key={source} className="h-4 w-4 text-muted-foreground" /> : null
                              })}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            {tracker.top_emotions && Array.isArray(tracker.top_emotions) && tracker.top_emotions.slice(0, 3).map((emotion, idx) => (
                            <Badge key={idx} className={`${getEmotionColor(emotion.emotion)} text-xs`}>
                              {emotion.emotion}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`h-4 w-4 ${status.color}`} />
                          <Badge variant="outline" className={`${status.color} border text-xs`}>
                            {status.label}
                          </Badge>
                        </div>
                        <div className={`text-4xl font-bold ${getScoreColor(roundedPositivity)} whitespace-nowrap`}>
                          {roundedPositivity !== null ? `${roundedPositivity}%` : "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="space-y-3">
                      {tracker.summary && (
                        <div className="bg-white border rounded-lg p-4">
                          <p className="text-sm text-gray-700">{tracker.summary}</p>
                        </div>
                      )}

                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-base font-semibold mb-3">Recent Tracker Hits</h4>
                        {loadingHits[tracker.tracker_id] ? (
                          <div className="text-center py-4">
                            <p className="text-sm text-muted-foreground">Loading hits...</p>
                          </div>
                        ) : trackerHits[tracker.tracker_id] && trackerHits[tracker.tracker_id].length > 0 ? (
                          <div className="space-y-3">
                            {trackerHits[tracker.tracker_id].map((hit) => {
                              const summarizedReason = getSummarizedReason(hit.reasoning)
                              const contentDetails = hit.content_details || {}
                              const { label, Icon } = getContentTypeDisplay(contentDetails.type || hit.content_type)

                              const colorName = getHitBorderColorClass(hit.primary_emotion?.emotion)

                              // Map color names to hex colors for inline styles
                              let borderColor = '#6b7280' // gray-500
                              if (colorName === 'green') {
                                borderColor = '#22c55e' // green-500
                              } else if (colorName === 'purple') {
                                borderColor = '#a855f7' // purple-500
                              } else if (colorName === 'red') {
                                borderColor = '#ef4444' // red-500
                              } else if (colorName === 'orange') {
                                borderColor = '#f97316' // orange-500
                              }

                              return (
                                <div
                                  key={hit.hit_id}
                                  className="pl-4 py-2"
                                  ref={(el) => {
                                    if (el) {
                                      el.style.setProperty('border-left', `4px solid ${borderColor}`, 'important')
                                    }
                                  }}
                                >
                                  <div className="flex items-start justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      {Icon && <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                                      <h5 className="font-medium text-sm">{label}</h5>
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                      {formatTimeAgo(hit.occurred_at)}
                                    </span>
                                  </div>
                                  {summarizedReason && (
                                    <p className="text-sm text-gray-600">{summarizedReason}</p>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-sm text-muted-foreground">No recent hits found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

